import {
  Question,
  type Questionnaire,
  type QuestionnairePage,
} from '@/lib/questionnaires/Questionnaire'
import { InferType, object, string } from 'yup'
import { mustAgree, mustSelectNo } from '@/shared/forms/YupValidators'
import { PrescriptionQuestionnaire } from '@/lib/apis/types'

export function BooleanYesNoTransformer(value: any) {
  if (value === 'true' || value === true) {
    return 'Yes'
  } else if (value === 'false' || value === false) {
    return 'No'
  }

  return value
}

function isNumberOrEmpty(value: string): boolean {
  if (value.trim().length === 0) return true

  return !Number.isNaN(Number.parseInt(value))
}

export namespace Prescription {
  export const Questionnaires: Questionnaire = {
    pages: [
      {
        questions: [
          Question.freeText(
            'condition',
            'For what condition are you taking this medicine? (e.g. High blood pressure, Depression, Diabetes)',
          ),
          Question.yesNo(
            'isPrescribedByRegularGP',
            'Was this medication prescribed by your regular GP?',
          ),
          Question.duration(
            'durationYear',
            'durationMonth',
            'How long have you been taking this for?',
          ),
          Question.yesNo(
            'isSameDose',
            'Are your symptoms well controlled with current dose?',
          ),
          Question.yesNo(
            'hasSideEffects',
            'Did you have any side effects from the medication?',
          ),
          Question.singleChoice(
            'isOrTryingToBePregnant',
            'Are you pregnant or trying to be pregnant?',
            [
              { id: 'Yes', label: 'Yes' },
              { id: 'No', label: 'No' },
              { id: 'NotApplicable', label: 'Not applicable' },
            ],
          ),
          Question.singleChoice('isBreastFeeding', 'Are you breast feeding?', [
            { id: 'Yes', label: 'Yes' },
            { id: 'No', label: 'No' },
            { id: 'NotApplicable', label: 'Not applicable' },
          ]),
        ],
      },
      {
        questions: [
          Question.yesNo(
            'hasSignificantMedicalCondition',
            'Do you have any significant current and/or past medical conditions?',
          ),
          Question.freeText(
            'significantMedicalConditions',
            'Please list your medical conditions in details.',
          ),
          Question.yesNo(
            'isOnOtherMedications',
            'Are you on any other medications in addition to this?',
          ),
          Question.freeText(
            'otherMedications',
            'Please list those medications in details.',
          ),
          Question.yesNo(
            'hasAllergy',
            'Are you allergic to anything such as medicine, food, etc?',
          ),
          Question.freeText(
            'allergies',
            'Please list your allergies in details.',
          ),
          Question.yesNo(
            'followUpAgreement',
            'Will you go and see your GP for the regular follow up in 1-3 months’ time?',
          ),
          Question.yesNo('inAustraliaAgreement', 'Are you in Australia?'),
          Question.yesNo(
            'allowsAutoNoteTakingSoftware',
            'Your practitioner may use note-taking software during your consultation to help them focus on you. Are you happy for them to do that?',
          ),
        ],
      },
    ],
  }

  export const Validator = object({
    condition: string()
      .default('')
      .required('You must indicate your medical condition.'),

    // TODO: Copy and paste the oneOf in the medical certificate fields too
    isPrescribedByRegularGP: string()
      .default('')
      .transform(BooleanYesNoTransformer)
      .oneOf(
        ['Yes', 'No'] as const,
        'You must select an answer for this question.',
      )
      .test(mustAgree('Sorry, we cannot issue prescription in this instance.')),
    isSameDose: string()
      .default('')
      .transform(BooleanYesNoTransformer)
      .oneOf(
        ['Yes', 'No'] as const,
        'You must select an answer for this question.',
      )
      .test(mustAgree('Sorry, we cannot issue prescription in this instance.')),
    hasSideEffects: string()
      .default('')
      .transform(BooleanYesNoTransformer)
      .oneOf(
        ['Yes', 'No'] as const,
        'You must select an answer for this question.',
      )
      .test(
        mustSelectNo('Sorry, we cannot issue prescription in this instance.'),
      ),
    isOrTryingToBePregnant: string()
      .default('')
      .oneOf(
        ['No', 'NotApplicable'] as const,
        'This requires a longer telehealth consultation by a doctor (coming soon).',
      ),
    isBreastFeeding: string()
      .default('')
      .oneOf(
        ['No', 'NotApplicable'] as const,
        'This requires a longer telehealth consultation by a doctor (coming soon).',
      ),
    durationYear: string().default(''),
    durationMonth: string()
      .default('')
      .test((value, context) => {
        const durationYear = context.parent.durationYear.trim()
        const durationMonth = value.trim()

        if (!isNumberOrEmpty(durationYear) || !isNumberOrEmpty(durationMonth))
          return context.createError({
            message: 'Years and months must be numbers.',
          })

        if (!durationYear && !durationMonth)
          return context.createError({
            message: 'Either year or month is required.',
          })

        return true
      }),
    hasSignificantMedicalCondition: string()
      .default('')
      .transform(BooleanYesNoTransformer)
      .oneOf(
        ['Yes', 'No'] as const,
        'You must select an answer for this question.',
      ),
    significantMedicalConditions: string()
      .default('')
      .test({
        test: (value, context) => {
          if (
            context.parent.hasSignificantMedicalCondition === 'Yes' &&
            value.trim().length === 0
          )
            return context.createError({ message: 'You need to answer this.' })
          return true
        },
      }),
    isOnOtherMedications: string()
      .default('')
      .transform(BooleanYesNoTransformer)
      .oneOf(
        ['Yes', 'No'] as const,
        'You must select an answer for this question.',
      ),
    otherMedications: string()
      .default('')
      .test({
        test: (value, context) => {
          if (
            context.parent.isOnOtherMedications === 'Yes' &&
            value.trim().length === 0
          )
            return context.createError({ message: 'You need to answer this.' })
          return true
        },
      }),
    hasAllergy: string()
      .default('')
      .transform(BooleanYesNoTransformer)
      .oneOf(
        ['Yes', 'No'] as const,
        'You must select an answer for this question.',
      ),
    allergies: string()
      .default('')
      .test({
        test: (value, context) => {
          if (context.parent.hasAllergy === 'Yes' && value.trim().length === 0)
            return context.createError({ message: 'You need to answer this.' })
          return true
        },
      }),
    followUpAgreement: string()
      .default('')
      .transform(BooleanYesNoTransformer)
      .oneOf(
        ['Yes', 'No'] as const,
        'You must select an answer for this question.',
      )
      .test(mustAgree('Sorry, we cannot issue prescription in this instance.')),
    inAustraliaAgreement: string()
      .default('')
      .transform(BooleanYesNoTransformer)
      .oneOf(
        ['Yes', 'No'] as const,
        'You must select an answer for this question.',
      )
      .test(mustAgree('Sorry, we cannot issue prescription in this instance.')),
    allowsAutoNoteTakingSoftware: string()
      .default('')
      .transform(BooleanYesNoTransformer)
      .oneOf(
        ['Yes', 'No'] as const,
        'You must select an answer for this question.',
      ),
  })

  export interface QuestionnaireValues extends InferType<typeof Validator> {}

  export function createQuestions(
    page: number,
    values: QuestionnaireValues,
  ): QuestionnairePage {
    if (page === 1) {
      const hiddenQuestions = new Set<string>()
      if (values.hasSignificantMedicalCondition !== 'Yes')
        hiddenQuestions.add('significantMedicalConditions')
      if (values.isOnOtherMedications !== 'Yes')
        hiddenQuestions.add('otherMedications')
      if (values.hasAllergy !== 'Yes') hiddenQuestions.add('allergies')

      return {
        questions: (
          Questionnaires.pages[page] as { questions: Array<Question> }
        ).questions.filter((question) => {
          return !('key' in question && hiddenQuestions.has(question.key))
        }),
      }
    }
    return Questionnaires.pages[page]
  }

  export function formToDto(
    values: QuestionnaireValues,
  ): PrescriptionQuestionnaire {
    return {
      condition: values.condition,
      isPrescribedByRegularGP: values.isPrescribedByRegularGP === 'Yes',
      isSameDose: values.isSameDose === 'Yes',
      hasSideEffects: values.hasSideEffects === 'Yes',
      isOrTryingToBePregnant: values.isOrTryingToBePregnant,
      isBreastFeeding: values.isBreastFeeding,
      durationYear:
        values.durationYear.trim().length > 0
          ? Number.parseInt(values.durationYear)
          : null,
      durationMonth:
        values.durationMonth.trim().length > 0
          ? Number.parseInt(values.durationMonth)
          : null,
      hasSignificantMedicalCondition:
        values.hasSignificantMedicalCondition === 'Yes',
      significantMedicalConditions: values.significantMedicalConditions,
      isOnOtherMedications: values.isOnOtherMedications === 'Yes',
      otherMedications: values.otherMedications,
      hasAllergy: values.hasAllergy === 'Yes',
      allergies: values.allergies,
      followUpAgreement: values.followUpAgreement === 'Yes',
      inAustraliaAgreement: values.inAustraliaAgreement === 'Yes',
    }
  }
}
